import type { ReactNode } from 'react';
import Sidebar from './Sidebar';
import type { NavItem } from './Sidebar';
import BottomNav from './BottomNav';
import Header from './Header';
import styles from './AppLayout.module.css';

interface AppLayoutProps {
  children: ReactNode;
  navItems: NavItem[];
  role: 'ap' | 'admin';
  userDisplayName: string;
  /**
   * Optional label saying WHAT `userDisplayName` is — the AP shell passes
   * "Provider record" or "Signed in as" so the resolved AP_Master row and the
   * Entra guest identity can never be read as the same fact again (ISS-032).
   * AdminShell passes none; an internal user's Entra name needs no caption.
   *
   * Sidebar only. The mobile Header shows an avatar initial, not a name, so
   * there is nothing there to mislabel — but it still receives the same
   * `userDisplayName`, which keeps the initial meaningful.
   */
  userDisplayCaption?: string;
  onLogout: () => void;
}

export default function AppLayout({
  children,
  navItems,
  role,
  userDisplayName,
  userDisplayCaption,
  onLogout,
}: AppLayoutProps) {
  const title = role === 'admin' ? 'Harmony Admin' : 'Harmony AP';

  return (
    <div className={styles.layout}>
      {/* Desktop: Sidebar */}
      <div className={styles.desktopOnly}>
        <Sidebar
          navItems={navItems}
          userDisplayName={userDisplayName}
          userDisplayCaption={userDisplayCaption}
          onLogout={onLogout}
        />
      </div>

      {/* Mobile: Header */}
      <div className={styles.mobileOnly}>
        <Header
          title={title}
          userDisplayName={userDisplayName}
          onLogout={onLogout}
        />
      </div>

      {/* Main content */}
      <main className={styles.main}>
        {children}
      </main>

      {/* Mobile: Bottom nav */}
      <div className={styles.mobileOnly}>
        <BottomNav navItems={navItems} />
      </div>
    </div>
  );
}
